import { ReactNode } from 'react';
import { BottomNavigation } from './BottomNavigation';
import { DesktopSidebar } from './DesktopSidebar';
import { DraftIndicator } from './DraftIndicator';
import { OfflineIndicator } from './OfflineIndicator';
import { TrialBanner } from '@/components/subscription/TrialBanner';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/lib/utils';

interface MainLayoutProps {
  children: ReactNode;
  showFab?: boolean;
  hideFab?: boolean;
  /**
   * Largura do conteúdo no desktop.
   *
   * `form` existe porque numa tela de formulário o problema do monitor grande
   * é o OPOSTO do de uma lista: um campo de texto esticado por ~980px é pior
   * de preencher do que um de 512px — o olho perde a linha entre o rótulo e o
   * fim do campo. Listas ganham com a largura; formulários, não.
   *
   * Telas de formulário que montam DUAS COLUNAS (o orçamento, com o total
   * fixo à direita) continuam no `default`: lá a largura extra é usada, não
   * desperdiçada.
   */
  width?: 'default' | 'form';
}

/**
 * Casca das telas autenticadas. Duas formas, decididas pela LARGURA (não pelo
 * domínio): abaixo de `lg` é o app de celular — conteúdo em coluna e navegação
 * inferior; a partir de `lg` a navegação vira menu lateral fixo e o conteúdo
 * ganha largura.
 *
 * Duas decisões que parecem detalhe e não são:
 *
 * 1. **`px-4 py-6` continua igual nas duas larguras.** Os ~23 heros do desenho
 *    oficial sangram com `-mx-4 -mt-6` casando exatamente com este padding
 *    (#159/#182). Aumentar o respiro no desktop deixaria cada um deles com uma
 *    faixa de fundo em volta, em vez de encostar na borda do conteúdo.
 *
 * 2. **A navegação inferior não é escondida por CSS, ela não é MONTADA.** No
 *    desktop ela não existe no DOM — nada de barra flutuante invisível
 *    capturando clique, e o `--bottom-nav-h` zerado (ver `index.css`) reancora
 *    de uma vez os toasts (#26) e as 8 barras de ação `sticky` que se apoiam
 *    nele.
 */
export function MainLayout({ children, width = 'default' }: MainLayoutProps) {
  const isDesktop = useIsDesktop();

  /**
   * 🔴 O CONTEÚDO OCUPA A LARGURA DISPONÍVEL — não há teto no modo `default`.
   *
   * Havia um `max-w-5xl` (1024px) centralizado aqui, e ele parecia certo
   * porque a verificação foi feita a 1280px: `256` de menu + `1024` de
   * conteúdo dá exatamente a tela, sem sobra. Acima disso o teto passa a
   * valer e o `mx-auto` divide o resto em dois vãos — num monitor de 2000px
   * são ~490px de fundo vazio de CADA lado, com o app espremido no meio. Foi
   * o usuário quem viu, comparando com o Asaas no mesmo monitor.
   *
   * Lição de medição: escolher UMA largura de teste esconde exatamente os
   * defeitos que só existem acima dela. Layout de desktop se confere em pelo
   * menos duas (aqui: 1280, 1920 e 2560).
   *
   * O modo `form` mantém teto de propósito — ali o problema é o oposto, um
   * campo de texto esticado por 1700px (ver o comentário da prop).
   */
  const caixaDesktop = width === 'form' ? 'max-w-3xl mx-auto' : 'w-full';

  return (
    <div className={cn('min-h-screen bg-background', !isDesktop && 'pb-layout-bottom')}>
      <OfflineIndicator />
      <TrialBanner />

      {isDesktop && <DesktopSidebar />}

      <div className={cn(isDesktop && 'pl-64')}>
        {/* `px-4` nas DUAS larguras: os ~23 heros do desenho oficial sangram
            com `-mx-4` casando exatamente com este padding (#159/#182). Um
            respiro maior no desktop deixaria cada hero com uma faixa de fundo
            em volta em vez de encostar na borda do conteúdo. */}
        <main className={cn('px-4 py-6', isDesktop ? caixaDesktop : 'mx-auto max-w-lg')}>
          {children}
        </main>
      </div>

      <DraftIndicator />
      {!isDesktop && <BottomNavigation />}
    </div>
  );
}
